import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { CATEGORICAL_TINTS, type CategoricalTint } from "../../lib/tints.js";

/* ---------------- Progress ----------------
   The inner bar takes a semantic tone (lib/tones.ts `dot`) or a categorical
   tint (lib/tints.ts `stripe`), never both. Colour lands on a sub-element, so
   it reads those shared maps rather than CVA. */

export type ProgressSize = "sm" | "md" | "lg";

const TRACK_HEIGHT: Record<ProgressSize, string> = {
  sm: "h-1",
  md: "h-1.5",
  lg: "h-2",
};

interface ProgressBaseProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `null` = unavailable: an empty track announced as "Unavailable". */
  value: number | null;
  max?: number;
  /** The rounded percent under the bar; "—" when `value` is null, nothing
   *  while `indeterminate`. */
  showLabel?: boolean;
  /** Track height: `sm` 4px, `md` 6px (default), `lg` 8px. */
  size?: ProgressSize;
  /** Work of unknown length: omits aria-valuenow and sweeps a segment across
   *  the track, held still under reduced motion. `value` is ignored. */
  indeterminate?: boolean;
  /** A reference value on the `max` scale, drawn as a 2px tick. */
  target?: number;
  /** Joins aria-valuetext when `target` draws a tick, e.g. "target 80%".
   *  Without it the tick is visual only. */
  targetLabel?: string;
}

/** Semantic `tone` or categorical `tint`: the type refuses both. A module
 *  tint is chrome only (ADR-0092), so Progress does not accept one. */
type ProgressColourProps =
  | {
      /** Semantic colour. Default `healthy`. */
      tone?: StatusTone;
      tint?: undefined;
    }
  | {
      tone?: undefined;
      /** Decorative identity: `bg-pb-tint-N`. Never a status. */
      tint: CategoricalTint;
    };

export type ProgressProps = ProgressBaseProps & ProgressColourProps;

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  function Progress(
    {
      value,
      max = 100,
      showLabel,
      tone = "healthy",
      tint,
      size = "md",
      indeterminate = false,
      target,
      targetLabel,
      className,
      // The accessible name belongs on the role="progressbar" node, not the
      // wrapper (axe: aria-progressbar-name / aria-prohibited-attr).
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...rest
    },
    ref,
  ) {
    // Sanitize before computing percent/ARIA: max must be a positive finite
    // number, value a finite number clamped into [0, max].
    const safeMax = Number.isFinite(max) && max > 0 ? max : 100;
    const clamp = (n: number) => Math.max(0, Math.min(safeMax, n));
    const unavailable = value === null && !indeterminate;
    const safeValue =
      typeof value === "number" && Number.isFinite(value) ? clamp(value) : 0;
    const pct = (safeValue / safeMax) * 100;

    const hasTarget = typeof target === "number" && Number.isFinite(target);
    const targetPct = hasTarget ? (clamp(target) / safeMax) * 100 : 0;
    const tickLabel = hasTarget && targetLabel ? targetLabel : undefined;
    const ownText = unavailable
      ? "Unavailable"
      : tickLabel && !indeterminate
        ? `${Math.round(pct)}%`
        : undefined;
    const valueText =
      [ownText, tickLabel].filter(Boolean).join(", ") || undefined;

    const fill =
      tint === undefined ? TONES[tone].dot : CATEGORICAL_TINTS[tint].stripe;

    return (
      <div ref={ref} className={cn("w-full", className)} {...rest}>
        <div
          className={cn(
            TRACK_HEIGHT[size],
            "rounded-full bg-pb-card-elevated overflow-hidden",
            (indeterminate || hasTarget) && "relative",
          )}
          role="progressbar"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-valuemin={0}
          aria-valuenow={indeterminate || unavailable ? undefined : safeValue}
          aria-valuemax={safeMax}
          aria-valuetext={valueText}
        >
          {indeterminate ? (
            // The segment's 30% insets are the geometry the pb-indeterminate
            // keyframes are written for (theme.css).
            <div
              className={cn(
                "absolute inset-y-0 inset-x-3/10 pb-indeterminate",
                fill,
              )}
            />
          ) : (
            <div
              className={cn(
                // ADR-0091: only the width moves. `transition-all` also animated
                // the tone colour and any layout change, which is why a tone
                // switch used to smear.
                "h-full transition-[width] duration-motion-state ease-motion-standard",
                fill,
              )}
              style={{ width: `${pct}%` }}
            />
          )}
          {hasTarget && (
            // Centred on target/max, kept whole inside the clipped track. Text
            // ink, not muted: a muted tick vanishes on the neutral fill.
            <div
              aria-hidden="true"
              className="absolute inset-y-0 w-0.5 bg-pb-text"
              style={{
                insetInlineStart: `clamp(0px, calc(${targetPct}% - 1px), calc(100% - 2px))`,
              }}
            />
          )}
        </div>
        {showLabel && !indeterminate && (
          // The dash is visual; the progressbar already says "Unavailable".
          <div
            className="text-dense-1 text-pb-text-muted pb-tabular mt-1"
            aria-hidden={unavailable || undefined}
          >
            {unavailable ? "—" : <>{Math.round(pct)}%</>}
          </div>
        )}
      </div>
    );
  },
);
